import { useId } from 'react';
import { Link } from 'react-router-dom';
import { useTravelText } from '../../app/TravelLocaleContext';
import {
  GUIDED_TASK_STATUS,
  derivePreparationTasks,
  guidedWorkspaceMode,
  preparationProgress,
  primaryPreparationTask,
} from './journeyGuidedTasksModel';

const STATUS_MARK = Object.freeze({
  [GUIDED_TASK_STATUS.DONE]: '✓',
  [GUIDED_TASK_STATUS.OPTIONAL_ADDED]: '✓',
  [GUIDED_TASK_STATUS.TODO]: '○',
  [GUIDED_TASK_STATUS.UNKNOWN]: '?',
  [GUIDED_TASK_STATUS.OPTIONAL_EMPTY]: '·',
  [GUIDED_TASK_STATUS.ENTRY]: '·',
});

function focusSection(id) {
  if (typeof document === 'undefined') return;
  const node = document.getElementById(id);
  if (!node) return;
  const reduceMotion = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (typeof node.scrollIntoView === 'function') {
    node.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
  }
  try { node.focus({ preventScroll: true }); } catch { node.focus(); }
}

const G = 'travel.journeyWorkspace.guide';

function statusLabel(statusText, { t, tf, tp }) {
  if (!statusText) return '';
  if (statusText.count != null) return tp(statusText.key, statusText.count, statusText.values);
  return statusText.values ? tf(statusText.key, statusText.values) : t(statusText.key);
}

function TaskAction({ action, onEditBasic, primary = false, testId }) {
  const { t } = useTravelText();
  if (!action) return null;
  const className = primary ? 'journey-guide__action journey-guide__action--primary' : 'journey-guide__action';
  if (action.kind === 'edit-basic') {
    return <button type="button" className={className} data-testid={testId} onClick={() => onEditBasic(action.focusField)}>{t(action.labelKey)}</button>;
  }
  return <Link className={className} data-testid={testId} to={action.to}>{t(action.labelKey)}</Link>;
}

function AllToolsButton() {
  const { t } = useTravelText();
  return (
    <button
      type="button"
      className="journey-guide__all-tools"
      data-testid="journey-guide-all-tools"
      onClick={() => focusSection('journey-all-tools')}
    >
      {t(`${G}.allTools`)}
    </button>
  );
}

function PrepareGuide({ journey, schedule, scheduleKnown, accommodations, transports, onEditBasic, headingId }) {
  const progressId = useId();
  const text = useTravelText();
  const { t, tf } = text;
  const tasks = derivePreparationTasks({ journey, schedule, scheduleKnown, accommodations, transports });
  const progress = preparationProgress(tasks);
  const primary = primaryPreparationTask(tasks);
  const others = tasks.filter((task) => task.key !== primary?.key);
  const percent = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <>
      <h3 id={headingId}>{t(`${G}.prepare.heading`)}</h3>
      <p id={progressId} className="journey-guide__progress-text" data-testid="journey-guide-progress">
        {tf(progress.textKey, { done: progress.done, total: progress.total })}
      </p>
      <div
        className="journey-guide__progress"
        role="progressbar"
        aria-labelledby={progressId}
        aria-valuemin={0}
        aria-valuemax={progress.total}
        aria-valuenow={progress.done}
      >
        <span style={{ width: `${percent}%` }} />
      </div>
      {progress.complete ? (
        <p className="journey-guide__note" data-testid="journey-guide-complete">{t(`${G}.prepare.complete`)}</p>
      ) : null}
      {progress.hasUnknown ? (
        <p className="journey-guide__note">{t(`${G}.prepare.unknownNote`)}</p>
      ) : null}

      {primary ? (
        <article className="journey-guide__primary" data-testid="journey-guide-primary" data-task={primary.key}>
          <p className="journey-guide__eyebrow">{t(primary.required ? `${G}.prepare.nextStep` : `${G}.prepare.nextStepOptional`)}</p>
          <h4>{t(primary.titleKey)}</h4>
          <p>{t(primary.descriptionKey)}</p>
          <p className="journey-guide__status" data-status={primary.status}>{statusLabel(primary.statusText, text)}</p>
          <div className="journey-guide__actions">
            {primary.action ? (
              <TaskAction action={primary.action} onEditBasic={onEditBasic} primary testId="journey-guide-primary-action" />
            ) : (
              <p className="journey-guide__note">{t(primary.unavailableHintKey)}</p>
            )}
            <TaskAction action={primary.secondaryAction} onEditBasic={onEditBasic} />
          </div>
        </article>
      ) : null}

      <div className="journey-guide__next" data-testid="journey-guide-next">
        <h4>{t(`${G}.prepare.upNext`)}</h4>
        <ul>
          {others.map((task) => (
            <li key={task.key} data-task={task.key} data-status={task.status}>
              <span className="journey-guide__mark" aria-hidden="true">{STATUS_MARK[task.status]}</span>
              <span className="journey-guide__next-body">
                <strong>{t(task.titleKey)}</strong>
                <small>{statusLabel(task.statusText, text)}</small>
                {task.hintText ? (
                  <small data-testid={`journey-guide-hint-${task.key}`}>{statusLabel(task.hintText, text)}</small>
                ) : null}
              </span>
              {task.action ? <TaskAction action={task.action} onEditBasic={onEditBasic} /> : null}
            </li>
          ))}
        </ul>
      </div>
      <AllToolsButton />
    </>
  );
}

/**
 * 總覽任務首頁：依 lifecycle 突出一項主要任務；不取代各功能頁，也不新增 API。
 * 獨立貼文、Memory 與公開故事不列為旅程任務。
 */
export default function JourneyGuidedTasks({
  journey, schedule, scheduleKnown, accommodations, transports, onEditBasic,
}) {
  const headingId = useId();
  const { t } = useTravelText();
  const mode = guidedWorkspaceMode(journey?.lifecycleStatus);
  if (mode === 'unknown') return null;
  const id = String(journey.journeyId);

  return (
    <section className="journey-guide" aria-labelledby={headingId} data-testid="journey-guide" data-mode={mode}>
      {mode === 'prepare' ? (
        <PrepareGuide
          journey={journey}
          schedule={schedule}
          scheduleKnown={scheduleKnown}
          accommodations={accommodations}
          transports={transports}
          onEditBasic={onEditBasic}
          headingId={headingId}
        />
      ) : null}

      {mode === 'traveling' ? (
        <>
          <h3 id={headingId}>{t(`${G}.traveling.heading`)}</h3>
          <article className="journey-guide__primary" data-testid="journey-guide-primary" data-task="today">
            <p className="journey-guide__eyebrow">{t(`${G}.traveling.eyebrow`)}</p>
            <h4>{t(`${G}.traveling.title`)}</h4>
            <p>{t(`${G}.traveling.body`)}</p>
            <div className="journey-guide__actions">
              <Link className="journey-guide__action journey-guide__action--primary" data-testid="journey-guide-primary-action" to={`/journeys/${id}/today`}>{t(`${G}.traveling.actionToday`)}</Link>
              <Link className="journey-guide__action" to={`/journeys/${id}/journal`}>{t('travel.journeyWorkspace.recordJourney')}</Link>
            </div>
          </article>
          <AllToolsButton />
        </>
      ) : null}

      {mode === 'completed' ? (
        <>
          <h3 id={headingId}>{t(`${G}.completed.heading`)}</h3>
          <article className="journey-guide__primary" data-testid="journey-guide-primary" data-task="wrap-up">
            <p className="journey-guide__eyebrow">{t(`${G}.completed.eyebrow`)}</p>
            <h4>{t(`${G}.completed.title`)}</h4>
            <p>{t(`${G}.completed.body`)}</p>
            <div className="journey-guide__actions">
              <Link className="journey-guide__action journey-guide__action--primary" data-testid="journey-guide-primary-action" to={`/journeys/${id}/journal`}>{t(`${G}.completed.actionJournal`)}</Link>
              <button type="button" className="journey-guide__action" onClick={() => focusSection('journey-publication')}>
                {t(`${G}.completed.actionPublication`)}
              </button>
            </div>
          </article>
          <AllToolsButton />
        </>
      ) : null}

      {mode === 'closed' ? (
        <>
          <h3 id={headingId}>{t(journey.lifecycleStatus === 'ARCHIVED' ? `${G}.closed.archivedHeading` : `${G}.closed.cancelledHeading`)}</h3>
          <p className="journey-guide__note" data-testid="journey-guide-closed">
            {t(journey.lifecycleStatus === 'ARCHIVED' ? `${G}.closed.archivedBody` : `${G}.closed.cancelledBody`)}
          </p>
          <AllToolsButton />
        </>
      ) : null}
    </section>
  );
}
